/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @version 1.19.0
 * @since 1.19.0
 */

import { ChangeEvent, memo, useEffect, useRef, useState } from 'react';

import invoke from 'lodash/invoke';
import { useSelector } from 'react-redux';

import { icons } from '../../../../assets';
import { DropDownFilter, RangeInput } from '../../../../components/common';
import { EInputCursor, EInputKind, EInputType } from '../../../../components/common/Input/Input';
import { ICurrentInputProps } from '../../../../components/common/RangeInput/RangeInput';
import { makeSelectTranslationCommon } from '../../../internationalization/intSelectors';
import { useTableFilterApi } from '../../api';
import { useTableFilter } from './hooks/useTableFilter';
import {
    getFilterRangeRangeFilterValue,
    isFilterRangeRightValueGreater,
    isFilterRangeValueValid,
} from './utils/rangeUtils';


interface ITableFilterRange {
    name: string;
    isCurrency?: boolean;
}

const TableFilterRange = ({ name, isCurrency }: ITableFilterRange) => {

    const translationCommon = useSelector(makeSelectTranslationCommon());

    const dropDownRef = useRef(null);

    /* API */

    const apiFilter = useTableFilterApi();

    const {
        filters,
        selectedFilter,
    } = useTableFilter(name);

    const start = selectedFilter?.valueFrom;
    const end = selectedFilter?.valueTo;

    const [valueFrom, setValueFrom] = useState(start);
    const [valueFromValid, setValueFromValid] = useState(true);
    const [valueTo, setValueTo] = useState(end);
    const [valueToValid, setValueToValid] = useState(true);

    const regExp = new RegExp(isCurrency ? /^[\-]?\d+(?:[.,][\d]{1,2})?$/ : /^\d+$/);

    const isFilterChanged = () => valueFrom !== start || valueTo !== end;

    const inputHandlerLeft = (event: ChangeEvent<HTMLInputElement>) => {
        const text = event.target.value;

        setValueFrom(text);
        setValueFromValid(isFilterRangeRightValueGreater(regExp, text, valueTo)
            && isFilterRangeValueValid(regExp, text));
        setValueToValid(isFilterRangeRightValueGreater(regExp, text, valueTo)
            && isFilterRangeValueValid(regExp, valueTo));
    };

    const inputHandlerRight = (event: ChangeEvent<HTMLInputElement>) => {
        const text = event.target.value;

        setValueTo(text);
        setValueFromValid(isFilterRangeRightValueGreater(regExp, valueFrom, text)
            && isFilterRangeValueValid(regExp, valueFrom));
        setValueToValid(isFilterRangeRightValueGreater(regExp, valueFrom, text)
            && isFilterRangeValueValid(regExp, text));
    };

    const okButtonHandler = () => {
        if (valueFromValid && valueToValid && isFilterChanged()) {
            apiFilter.filter({ ...filters, [name]: { valueFrom, valueTo } });
        }
    };

    const onEnterHandler = () => {
        invoke(dropDownRef, 'current.componentRef.current.handleOkButtonClick');
    };

    const onCloseHandler = () => {
        setValueFrom(start);
        setValueFromValid(true);
        setValueTo(end);
        setValueToValid(true);
    };

    useEffect(() => {
        setValueFrom(start);
        setValueTo(end);
    }, [start, end]);

    const filterValue = getFilterRangeRangeFilterValue(start, end);
    const icon = filterValue ? '' : icons.arrowListDownSmall;

    const inputLeft: ICurrentInputProps = {
        input: {
            autoFocus: true,
            cursor: EInputCursor.auto,
            kind: valueFromValid ? EInputKind.normal : EInputKind.error,
            label: translationCommon.commonTable.from,
            onChange: inputHandlerLeft,
            placeholder: translationCommon.commonTable.from,
            type: EInputType.small,
            value: valueFrom,
        },
    };

    const inputRight: ICurrentInputProps = {
        input: {
            cursor: EInputCursor.auto,
            kind: valueToValid ? EInputKind.normal : EInputKind.error,
            label: translationCommon.commonTable.to,
            onChange: inputHandlerRight,
            placeholder: translationCommon.commonTable.to,
            type: EInputType.small,
            value: valueTo,
        },
    };

    return (
        <DropDownFilter
            renderInBody
            showCloseButton
            widthAuto
            filterButtonLabel={filterValue}
            icon={icon}
            kind={EInputKind.normal}
            okButtonDisabled={(!valueFromValid || !valueToValid)}
            ref={dropDownRef}
            translation={({
                ...translationCommon.dropDown,
                placeholder: translationCommon.range.placeholder,
            })}
            onCloseClick={onCloseHandler}
            onOkClick={okButtonHandler}
        >
            <RangeInput
                left={inputLeft}
                right={inputRight}
                onEnter={onEnterHandler}
            />
        </DropDownFilter>
    );
};

export default memo(TableFilterRange);
